import React, { useContext, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Box, Link, Container } from 'pcln-design-system'
import {
  ContentfulRichText,
  ICallToActionType,
  IToutType
} from '@pcln/contentful-components'
import { MerchModule } from '@pcln/marketing'
import analytics from '@/shared-utils/analytics'
import {
  ANALYTICS_CATEGORY_FLIGHTS,
  ANALYTICS_CATEGORY_HOTELS,
  ANALYTICS_CATEGORY_PACKAGES,
  ANALYTICS_CATEGORY_HOME
} from '@/constants/analytics'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import AppleStore from './graphics/AppleStoreSvg'
import PlayStore from './graphics/PlayStoreSvg'

const ANALYTICS_CATEGORIES = {
  '/flights': ANALYTICS_CATEGORY_FLIGHTS,
  '/hotels': ANALYTICS_CATEGORY_HOTELS,
  '/vacationpackages': ANALYTICS_CATEGORY_PACKAGES,
  '/': ANALYTICS_CATEGORY_HOME
} as const

type ANALYTICS_CATEGORY_TYPE =
  | typeof ANALYTICS_CATEGORY_FLIGHTS
  | typeof ANALYTICS_CATEGORY_HOTELS
  | typeof ANALYTICS_CATEGORY_PACKAGES
  | typeof ANALYTICS_CATEGORY_HOME

const SVGS: any = {
  AppleStore,
  PlayStore
}

function renderSVG(svgName: 'AppleStore' | 'PlayStore') {
  const SVG = SVGS[svgName] || React.Fragment
  return <SVG />
}

const MerchModuleWrapper = ({
  backgroundColor = 'background.light',
  callsToAction,
  displayOption,
  body,
  credCardModWrapDefaultDisplay = false,
  image,
  googleAnalyticsTracking,
  ...props
}: IToutType & {
  backgroundColor?: string
  credCardModWrapDefaultDisplay?: boolean
}) => {
  const router = useRouter()
  const pathname = router?.pathname
  const googleAnalyticsCategory: ANALYTICS_CATEGORY_TYPE =
    ANALYTICS_CATEGORIES[pathname as keyof typeof ANALYTICS_CATEGORIES] || ''

  useEffect(() => {
    if (googleAnalyticsTracking?.action && googleAnalyticsTracking?.label) {
      analytics.fireEvent({
        category: googleAnalyticsTracking.category
          ? googleAnalyticsTracking.category
          : 'promotion',
        action: googleAnalyticsTracking.action,
        label: googleAnalyticsTracking.label
      })
    }
  }, [googleAnalyticsTracking])

  const moduleButtons: JSX.Element | null = callsToAction ? (
    <>
      {callsToAction.map((cta: ICallToActionType) => {
        const linkVariation = cta.svgName ? 'link' : 'fill'
        return (
          <Link
            mr={2}
            key={cta.ariaLabel}
            variation={linkVariation}
            href={cta.url}
            target={cta.openInNewTab ? '_blank' : undefined}
            rel={cta.openInNewTab ? 'noopener' : undefined}
            aria-label={cta.ariaLabel}
            onClick={() => {
              if (cta.googleAnalyticsTracking?.action) {
                analytics.fireEvent({
                  action: cta.googleAnalyticsTracking.action,
                  category:
                    cta.googleAnalyticsTracking.category ||
                    googleAnalyticsCategory,
                  label: cta.googleAnalyticsTracking.label
                })
              }
            }}
          >
            {cta.svgName
              ? renderSVG(cta.svgName as 'AppleStore' | 'PlayStore')
              : cta.label}
          </Link>
        )
      })}
    </>
  ) : null

  const { isMobile } = useContext(BootstrapDataContext)

  return (
    <>
      {!credCardModWrapDefaultDisplay ? (
        <Container>
          <Box
            color={backgroundColor}
            mx={isMobile ? 0 : 4}
            mt={isMobile ? '40px' : 0}
            mb={4}
            p={4}
            borderRadius="xl"
            boxShadowSize="sm"
          >
            <MerchModule
              {...props}
              textAlign={displayOption}
              imageUrl={image && `${image?.asset?.file?.url}?h=600&q=70`}
              imageAlt={image?.altText}
              callToAction={image?.callToAction}
              headingTextStyle="heading2"
              taglineTextStyle="overline"
              bodyLineHeight="25px"
              headingMB="0px"
              borderRadius="xl"
              body={
                <ContentfulRichText
                  color="text.dark"
                  textStyle="article"
                  fontSize={3}
                  body={body as any}
                />
              }
              actions={moduleButtons}
            />
          </Box>
        </Container>
      ) : (
        <Box color={backgroundColor} py={4} px={3}>
          <MerchModule
            {...props}
            textAlign={displayOption}
            imageUrl={image && `${image?.asset?.file?.url}?h=600&q=70`}
            imageAlt={image?.altText}
            callToAction={image?.callToAction}
            headingTextStyle="heading2"
            taglineTextStyle="overline"
            bodyLineHeight="25px"
            headingMB="0px"
            body={
              <ContentfulRichText
                color="text.dark"
                textStyle="article"
                fontSize={3}
                body={body as any}
              />
            }
            actions={moduleButtons}
          />
        </Box>
      )}
    </>
  )
}

export default MerchModuleWrapper
